/**
 * White Coffee - Interactive Controller & Animations
 */

const initWhiteCoffee = () => {
  // 1. Preloader / Intro Animation Sequence
  const introEl = document.querySelector('.intro');
  const headerEl = document.querySelector('.header');

  if (introEl) {
    document.body.classList.add('u-body-scroll-fixed');

    // Trigger rise animation immediately
    setTimeout(() => {
      introEl.classList.add('is-active');
    }, 60);

    let introFinished = false;
    const finishIntro = () => {
      if (introFinished) return;
      introFinished = true;

      introEl.classList.add('is-leaving');
      document.body.classList.remove('u-body-scroll-fixed');
      if (headerEl) headerEl.classList.add('is-visible');

      // Trigger hero title reveal smoothly as the intro begins to leave
      const heroSurtitle = document.querySelector('.homepage-hero__surtitle .visible');
      if (heroSurtitle) setTimeout(() => heroSurtitle.classList.add('is-visible'), 50);

      const heroLines = document.querySelectorAll('.homepage-hero__title .visible');
      heroLines.forEach((line, index) => {
        setTimeout(() => line.classList.add('is-visible'), 100 + index * 180);
      });

      const heroSub = document.querySelectorAll('.homepage-hero__subtitle .visible');
      heroSub.forEach((sub, index) => {
        setTimeout(() => sub.classList.add('is-visible'), 500 + index * 80);
      });

      const heroBtn = document.querySelector('.homepage-hero .t-button')?.closest('.visible') || document.querySelector('.homepage-hero .t-button');
      if (heroBtn) setTimeout(() => heroBtn.classList.add('is-visible'), 700);

      setTimeout(() => {
        introEl.style.display = 'none';
        const pageEl = document.querySelector('.page');
        if (pageEl) pageEl.classList.remove('is-intro');
      }, 1400);
    };

    // Auto-advance after 2.6s
    const introTimer = setTimeout(finishIntro, 2600);

    // Allow user scroll or click to advance promptly after 200ms
    setTimeout(() => {
      const onScrollAction = (e) => {
        if (Math.abs(e.deltaY || 0) > 10 || e.type === 'keydown' || e.type === 'touchstart' || e.type === 'click') {
          clearTimeout(introTimer);
          finishIntro();
          window.removeEventListener('wheel', onScrollAction);
          window.removeEventListener('keydown', onScrollAction);
          window.removeEventListener('touchstart', onScrollAction);
          introEl.removeEventListener('click', finishIntro);
        }
      };
      window.addEventListener('wheel', onScrollAction, { passive: true });
      window.addEventListener('keydown', onScrollAction, { passive: true });
      window.addEventListener('touchstart', onScrollAction, { passive: true });
      introEl.addEventListener('click', finishIntro);
    }, 200);
  } else {
    if (headerEl) headerEl.classList.add('is-visible');
    const heroSurtitle = document.querySelector('.homepage-hero__surtitle .visible');
    if (heroSurtitle) setTimeout(() => heroSurtitle.classList.add('is-visible'), 50);

    const heroLines = document.querySelectorAll('.homepage-hero__title .visible');
    heroLines.forEach((line, index) => {
      setTimeout(() => line.classList.add('is-visible'), 100 + index * 180);
    });

    const heroSub = document.querySelectorAll('.homepage-hero__subtitle .visible');
    heroSub.forEach((sub, index) => {
      setTimeout(() => sub.classList.add('is-visible'), 500 + index * 80);
    });

    const heroBtn = document.querySelector('.homepage-hero .t-button')?.closest('.visible') || document.querySelector('.homepage-hero .t-button');
    if (heroBtn) setTimeout(() => heroBtn.classList.add('is-visible'), 700);
  }

  // Nuxt-equivalent Scroll Reveal Observer
  const initScrollVisibility = () => {
    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const target = entry.target;
          obs.unobserve(target);

          const visibleEl = target.classList.contains('visible') ? target : target.querySelector('.visible');
          if (!visibleEl) return;

          const titleContainer = target.closest('.homepage-hero__title, .home-products__title, .homepage-lifestyle__title, .homepage-store__title');
          if (titleContainer) {
            const lines = Array.from(titleContainer.querySelectorAll('.visible'));
            const index = lines.indexOf(visibleEl);
            const delay = index >= 0 ? index * 180 : 0;
            setTimeout(() => visibleEl.classList.add('is-visible'), delay);
          } else {
            const cardContainer = target.closest('.product-card');
            if (cardContainer) {
              const allCards = Array.from(document.querySelectorAll('.product-card'));
              const cardIdx = allCards.indexOf(cardContainer);
              const colIdx = cardIdx >= 0 ? cardIdx % 3 : 0;
              setTimeout(() => visibleEl.classList.add('is-visible'), colIdx * 120);
            } else {
              visibleEl.classList.add('is-visible');
            }
          }
        }
      });
    }, {
      rootMargin: '50px 0px -5% 0px',
      threshold: 0.05
    });

    document.querySelectorAll('.visible:not(.is-visible)').forEach(el => {
      if (introEl && el.closest('[class*="homepage-hero"]')) {
        return; // Do NOT trigger hero animations while intro covers the screen
      }
      if (el.closest('.catalog__title') || el.closest('.catalog__items') || el.closest('.daily-page-wrapper') || el.closest('.collab-page-wrapper')) {
        return; // Handled exclusively by synchronized page entrance choreography
      }
      const slit = el.closest('.u-overflow-hidden');
      observer.observe(slit || el);
    });
  };

  initScrollVisibility();

  // 2. Stores Accordion & Kinetic Drag/Auto-Defile Carousels (Marais, Faubourg, Turenne)
  class StoreCarouselController {
    constructor(expandableEl, onOpenCallback) {
      this.expandable = expandableEl;
      this.onOpenCallback = onOpenCallback;
      this.title = expandableEl.querySelector('.homepage-store__titleitem')?.textContent.trim();
      this.oppener = expandableEl.querySelector('.expandable__oppener');
      this.oppenheimer = expandableEl.querySelector('.expandable__oppenheimer');
      this.oppenheimerContent = expandableEl.querySelector('.expandable__oppenheimer__content');
      this.dragWrapper = expandableEl.querySelector('.u-overflow-y-invisible');
      this.dragEl = expandableEl.querySelector('.js-drag');
      this.useIcon = expandableEl.querySelector('.homepage-store__more__svg use');

      this.isOpen = this.expandable.classList.contains('is-open');
      this.isGrabbing = false;
      this.hasDrag = false;
      this.autoDefile = true;
      this.isOpenable = !!(this.dragEl && this.dragEl.children.length > 0);

      this.containerWidth = 0;
      this.contentWidth = 0;
      this.maxScroll = 0;

      this.currX = 0;
      this.smoothX = 0;
      this.startX = 0;
      this.dragStartCurrX = 0;
      this.lastTickTime = performance.now();
      this.rafId = null;

      this.init();
    }

    init() {
      if (!this.isOpenable) return;

      if (this.dragWrapper) {
        this.dragWrapper.classList.add('u-cursor-grab');
      }

      // Prevent native browser image drag and track image loads
      this.dragEl.querySelectorAll('img').forEach(img => {
        img.setAttribute('draggable', 'false');
        img.ondragstart = (e) => { e.preventDefault(); return false; };
        img.addEventListener('dragstart', e => e.preventDefault());
        if (!img.complete) {
          img.addEventListener('load', () => this.onResize(), { once: true });
          img.addEventListener('error', () => this.onResize(), { once: true });
        }
      });

      this.onMouseDown = this.onMouseDown.bind(this);
      this.onMouseMove = this.onMouseMove.bind(this);
      this.onMouseUp = this.onMouseUp.bind(this);

      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);

      this.onWheel = this.onWheel.bind(this);
      this.tick = this.tick.bind(this);

      if (this.dragWrapper) {
        this.dragWrapper.addEventListener('mousedown', this.onMouseDown);
        this.dragWrapper.addEventListener('touchstart', this.onTouchStart, { passive: false });
        this.dragWrapper.addEventListener('wheel', this.onWheel, { passive: true });
      }

      if (this.dragEl) {
        this.dragEl.addEventListener('mousedown', this.onMouseDown);
      }

      if (this.oppener) {
        this.oppener.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggle();
        });
      }

      if (window.ResizeObserver && this.dragEl) {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.isOpen) this.onResize();
        });
        this.resizeObserver.observe(this.dragEl);
      }

      this.onResize();

      if (this.isOpen) {
        const targetHeight = this.oppenheimerContent ? this.oppenheimerContent.offsetHeight : this.oppenheimer.scrollHeight;
        if (this.oppenheimer) {
          this.oppenheimer.style.maxHeight = (targetHeight || 520) + 'px';
          this.oppenheimer.style.opacity = '1';
        }
        if (this.useIcon) {
          this.useIcon.setAttribute('xlink:href', '#lessbold');
          this.useIcon.setAttribute('href', '#lessbold');
        }
        this.lastTickTime = performance.now();
        if (this.rafId) {
          cancelAnimationFrame(this.rafId);
          this.rafId = null;
        }
        this.rafId = requestAnimationFrame(this.tick);
      } else {
        if (this.oppenheimer) {
          this.oppenheimer.style.maxHeight = '0px';
          this.oppenheimer.style.opacity = '0';
        }
        if (this.useIcon) {
          this.useIcon.setAttribute('xlink:href', '#morebold');
          this.useIcon.setAttribute('href', '#morebold');
        }
      }
    }

    onResize() {
      if (!this.dragEl || !this.dragWrapper) return;
      this.containerWidth = this.dragWrapper.offsetWidth || window.innerWidth;
      
      let totalW = 0;
      const children = this.dragEl.children;
      for (let i = 0; i < children.length; i++) {
        const childW = children[i].offsetWidth || 300;
        totalW += childW + 24;
      }
      this.contentWidth = Math.max(this.dragEl.scrollWidth || 0, totalW);
      this.hasDrag = this.contentWidth > this.containerWidth || children.length > 3;
      this.maxScroll = -(this.contentWidth - this.containerWidth);
      if (this.maxScroll > 0) this.maxScroll = 0;
    }

    onMouseDown(e) {
      if (e.button !== 0) return;
      // Prevent browser native image drag and text selection
      e.preventDefault();

      this.isGrabbing = true;
      this.startX = e.pageX;
      this.dragStartCurrX = this.currX;

      if (this.dragWrapper) {
        this.dragWrapper.classList.add('is-grabbing', 'u-disable-selection');
      }
      document.body.classList.add('u-disable-selection');

      window.addEventListener('mousemove', this.onMouseMove);
      window.addEventListener('mouseup', this.onMouseUp);
      window.addEventListener('dragend', this.onMouseUp);
      window.addEventListener('blur', this.onMouseUp);
    }

    onMouseMove(e) {
      if (!this.isGrabbing) return;
      const deltaX = e.pageX - this.startX;
      this.currX = this.dragStartCurrX + deltaX;

      if (this.currX > 0) this.currX = 0;
      if (this.currX < this.maxScroll) this.currX = this.maxScroll;
    }

    onMouseUp() {
      if (!this.isGrabbing) return;
      this.isGrabbing = false;
      if (this.dragWrapper) {
        this.dragWrapper.classList.remove('is-grabbing', 'u-disable-selection');
      }
      document.body.classList.remove('u-disable-selection');

      window.removeEventListener('mousemove', this.onMouseMove);
      window.removeEventListener('mouseup', this.onMouseUp);
      window.removeEventListener('dragend', this.onMouseUp);
      window.removeEventListener('blur', this.onMouseUp);
    }

    onTouchStart(e) {
      if (!e.touches || e.touches.length === 0) return;
      this.isGrabbing = true;
      this.startX = e.touches[0].pageX;
      this.dragStartCurrX = this.currX;
      if (this.dragWrapper) this.dragWrapper.classList.add('is-grabbing');

      window.addEventListener('touchmove', this.onTouchMove, { passive: false });
      window.addEventListener('touchend', this.onTouchEnd);
      window.addEventListener('touchcancel', this.onTouchEnd);
    }

    onTouchMove(e) {
      if (!this.isGrabbing || !e.touches || e.touches.length === 0) return;
      const deltaX = e.touches[0].pageX - this.startX;
      if (Math.abs(deltaX) > 5 && e.cancelable) {
        e.preventDefault();
      }
      this.currX = this.dragStartCurrX + deltaX;
      if (this.currX > 0) this.currX = 0;
      if (this.currX < this.maxScroll) this.currX = this.maxScroll;
    }

    onTouchEnd() {
      this.isGrabbing = false;
      if (this.dragWrapper) this.dragWrapper.classList.remove('is-grabbing');
      window.removeEventListener('touchmove', this.onTouchMove);
      window.removeEventListener('touchend', this.onTouchEnd);
      window.removeEventListener('touchcancel', this.onTouchEnd);
    }

    onWheel(e) {
      if (!this.isOpen || !this.hasDrag) return;
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : (e.shiftKey ? e.deltaY : 0);
      if (Math.abs(delta) > 0) {
        this.currX -= delta * 0.8;
        if (this.currX > 0) this.currX = 0;
        if (this.currX < this.maxScroll) this.currX = this.maxScroll;
      }
    }

    tick(now) {
      if (!this.isOpen) {
        this.rafId = null;
        return;
      }

      this.rafId = null;

      const elapsed = (now && this.lastTickTime) ? (now - this.lastTickTime) : 16.667;
      this.lastTickTime = now || performance.now();
      const deltaRatio = Math.min(3, Math.max(0.5, elapsed / 16.667));

      // Dynamic geometry re-check if layout updated
      if (!this.hasDrag || this.maxScroll >= 0) {
        this.onResize();
      }

      if (this.hasDrag) {
        // Continuous auto-defile slowly to the left when not holding/dragging
        if (!this.isGrabbing && this.autoDefile) {
          this.currX -= 0.35 * deltaRatio;
          if (this.currX < this.maxScroll) {
            this.currX = this.maxScroll;
          }
        }

        if (this.currX > 0) this.currX = 0;
        if (this.currX < this.maxScroll) this.currX = this.maxScroll;

        this.smoothX += (this.currX - this.smoothX) * 0.1;
        const formatted = this.smoothX.toFixed(2);
        this.dragEl.style.transform = `translateX(${formatted}px) translateZ(0)`;
      }

      this.rafId = requestAnimationFrame(this.tick);
    }

    open() {
      if (this.onOpenCallback) this.onOpenCallback(this);

      this.isOpen = true;
      this.expandable.classList.add('is-open');
      if (this.useIcon) {
        this.useIcon.setAttribute('xlink:href', '#lessbold');
        this.useIcon.setAttribute('href', '#lessbold');
      }

      this.onResize();
      const targetHeight = this.oppenheimerContent ? this.oppenheimerContent.offsetHeight : this.oppenheimer.scrollHeight;
      this.oppenheimer.style.maxHeight = (targetHeight + 20) + 'px';
      this.oppenheimer.style.opacity = '1';

      this.currX = 0;
      this.smoothX = 0;
      if (this.dragEl) {
        this.dragEl.style.transform = 'translateX(0px) translateZ(0px)';
      }

      setTimeout(() => {
        this.onResize();
        if (this.oppenheimerContent) {
          this.oppenheimer.style.maxHeight = (this.oppenheimerContent.offsetHeight + 20) + 'px';
        }
      }, 100);

      setTimeout(() => {
        this.onResize();
      }, 450);

      this.lastTickTime = performance.now();
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      this.rafId = requestAnimationFrame(this.tick);
    }

    close() {
      this.isOpen = false;
      this.isGrabbing = false;
      this.expandable.classList.remove('is-open');
      if (this.useIcon) {
        this.useIcon.setAttribute('xlink:href', '#morebold');
        this.useIcon.setAttribute('href', '#morebold');
      }
      if (this.dragWrapper) {
        this.dragWrapper.classList.remove('is-grabbing', 'u-disable-selection');
      }
      document.body.classList.remove('u-disable-selection');

      if (this.oppenheimer) {
        this.oppenheimer.style.maxHeight = '0px';
        this.oppenheimer.style.opacity = '0';
      }

      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }
  }

  // Initialize all Store Expandables with coordinated accordion states
  const storeControllers = [];
  const onStoreOpen = (openedCtrl) => {
    storeControllers.forEach(ctrl => {
      if (ctrl !== openedCtrl && ctrl.isOpen) {
        ctrl.close();
      }
    });
  };

  document.querySelectorAll('.homepage-store .expandable').forEach(exp => {
    storeControllers.push(new StoreCarouselController(exp, onStoreOpen));
  });

  window.addEventListener('resize', () => {
    storeControllers.forEach(ctrl => {
      if (ctrl.isOpen) ctrl.onResize();
    });
  });

  // 3. Floating Lifestyle Gallery - Exact Scroll-Driven Parallax Convergence/Divergence
  const lsContainer = document.querySelector('.homepage-ls-image')?.closest('.u-relative');
  if (lsContainer) {
    const el1 = lsContainer.querySelector('.homepage-ls-image--1 .u-wrapper-panel');
    const el2 = lsContainer.querySelector('.homepage-ls-image--2 .u-wrapper-panel');
    const el3 = lsContainer.querySelector('.homepage-ls-image--3 .u-wrapper-panel');
    const el4 = lsContainer.querySelector('.homepage-ls-image--4 .u-wrapper-panel');

    const updateParallax = () => {
      const rect = lsContainer.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const containerCenter = rect.top + rect.height * 0.5;
      const viewportCenter = viewportHeight * 0.5;
      const delta = viewportCenter - containerCenter;

      if (el1) el1.style.transform = `translate3d(${delta * -0.048}px, 0, 0)`;
      if (el2) el2.style.transform = `translate3d(${delta * 0.036}px, 0, 0)`;
      if (el3) el3.style.transform = `translate3d(${delta * -0.037}px, 0, 0)`;
      if (el4) el4.style.transform = `translate3d(${delta * 0.042}px, 0, 0)`;
    };

    window.addEventListener('scroll', updateParallax, { passive: true });
    window.addEventListener('resize', updateParallax, { passive: true });
    updateParallax();
  }

  // 4. Page Transition Curtain & Theatrical Open Wipe (1:1 with white-coffee.com)
  const curtain = document.getElementById('page-curtain');
  const isTransitioning = document.documentElement.classList.contains('is-page-transitioning') || sessionStorage.getItem('pageTransition');

  if (curtain) {
    if (isTransitioning) {
      setTimeout(() => {
        try { sessionStorage.removeItem('pageTransition'); } catch(e) {}
        document.documentElement.classList.remove('is-page-transitioning');
        curtain.classList.add('sweep-up');
        setTimeout(() => {
          curtain.classList.remove('sweep-up');
        }, 850);
      }, 40);
    } else {
      // Direct open or refresh: ensure curtain is completely off-screen and unlocked
      document.documentElement.classList.remove('is-page-transitioning');
      curtain.classList.remove('sweep-down', 'initial-wipe');
    }

    // Intercept internal navigation clicks for theatrical page transitions
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (!link) return;
      if (link.target === '_blank' || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const href = link.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('javascript:')) return;

      try {
        const url = new URL(link.href, window.location.href);
        if (url.origin !== window.location.origin) return;
        if (url.pathname === window.location.pathname && url.search === window.location.search) {
          e.preventDefault();
          sessionStorage.setItem('pageTransition', '1');
          curtain.classList.add('sweep-down');
          setTimeout(() => {
            window.location.reload();
          }, 450);
          return;
        }

        e.preventDefault();
        sessionStorage.setItem('pageTransition', '1');
        curtain.classList.add('sweep-down');
        setTimeout(() => {
          window.location.href = url.href;
        }, 520);
      } catch (err) {}
    });
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWhiteCoffee);
} else {
  initWhiteCoffee();
}

// 4. Cart Drawer & AJAX Integration
class WhiteCoffeeCart {
  constructor() {
    this.cartDrawer = document.querySelector('.cart-drawer');
    this.cartCountElements = document.querySelectorAll('.cart-count');
    this.init();
  }

  init() {
    document.querySelectorAll('[data-add-to-cart]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = btn.getAttribute('data-variant-id');
        this.addItem(variantId, 1);
      });
    });
  }

  async addItem(variantId, quantity = 1) {
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      });
      await this.updateCartState();
      this.openDrawer();
    } catch (err) {
      console.error('Failed to add to cart:', err);
    }
  }

  async updateCartState() {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    this.cartCountElements.forEach(el => el.textContent = `(${cart.item_count})`);
  }

  openDrawer() {
    if (this.cartDrawer) this.cartDrawer.classList.add('is-active');
  }

  closeDrawer() {
    if (this.cartDrawer) this.cartDrawer.classList.remove('is-active');
  }
}

window.whiteCoffeeCart = new WhiteCoffeeCart();
